import { Refine, Authenticated } from "@refinedev/core";
import {
  ThemedLayoutV2,
  ErrorComponent,
  AuthPage,
  useNotificationProvider,
  RefineThemes,
} from "@refinedev/antd";
import routerProvider, {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import ptBR from "antd/locale/pt_BR";

import "@refinedev/antd/dist/reset.css";

import { dataProvider } from "@/providers/dataProvider";
import { authProvider } from "@/providers/authProvider";
import { accessControlProvider } from "@/providers/accessControlProvider";
import { melTheme } from "@/theme/antdTheme";
import { APP_NAME, APP_TAGLINE } from "@/theme/tokens";
import { MelTitle } from "@/components/Title";
import { MelHeader } from "@/components/Header";
import { resources } from "@/resources/definitions";
import { Overview } from "@/pages/overview";

import {
  UserList,
  UserShow,
  UserEdit,
  PlanList,
  PlanShow,
  EntryList,
  EntryShow,
  BrainList,
  BrainShow,
  AiMetricsList,
  CorrectionList,
  FeatureFlagList,
  FeatureFlagEdit,
  HealthList,
  AuditLogList,
} from "@/resources";

/**
 * melapp-admin — App Refine.
 *
 * - dataProvider: simple-rest → `${VITE_API_URL}/admin` (Bearer access token).
 * - authProvider: login na melapp-api, guarda role=admin (RolesGuard na API).
 * - accessControlProvider: defesa-em-profundidade no cliente.
 * - tema: paleta AQUA/CIANO glassmorphism (melTheme) + locale pt-BR.
 *
 * Recursos: users, plans, entries (moderação), brains (moderação),
 *   ai-metrics (dashboard de custo de IA), corrections (qualidade), feature-flags,
 *   health. Ver src/resources/definitions.tsx.
 */
export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConfigProvider
        theme={{ ...RefineThemes.Blue, ...melTheme }}
        locale={ptBR}
      >
        <AntdApp>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            routerProvider={routerProvider}
            notificationProvider={useNotificationProvider}
            resources={resources}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              title: { text: APP_NAME, icon: <img src="/mel-icon.svg" width={20} /> },
            }}
          >
            <Routes>
              {/* Área autenticada (role=admin) */}
              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <ThemedLayoutV2
                      Title={({ collapsed }) => <MelTitle collapsed={collapsed} />}
                      Header={() => <MelHeader />}
                    >
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route index element={<Overview />} />

                <Route path="/users">
                  <Route index element={<UserList />} />
                  <Route path="show/:id" element={<UserShow />} />
                  <Route path="edit/:id" element={<UserEdit />} />
                </Route>

                <Route path="/plans">
                  <Route index element={<PlanList />} />
                  <Route path="show/:id" element={<PlanShow />} />
                </Route>

                <Route path="/entries">
                  <Route index element={<EntryList />} />
                  <Route path="show/:id" element={<EntryShow />} />
                </Route>

                <Route path="/brains">
                  <Route index element={<BrainList />} />
                  <Route path="show/:id" element={<BrainShow />} />
                </Route>

                <Route path="/ai-metrics" element={<AiMetricsList />} />
                <Route path="/corrections" element={<CorrectionList />} />

                <Route path="/feature-flags">
                  <Route index element={<FeatureFlagList />} />
                  <Route path="edit/:id" element={<FeatureFlagEdit />} />
                </Route>

                <Route path="/audit-log" element={<AuditLogList />} />

                <Route path="/health" element={<HealthList />} />

                <Route path="*" element={<ErrorComponent />} />
              </Route>

              {/* Área pública (login) */}
              <Route
                element={
                  <Authenticated key="auth-pages" fallback={<Outlet />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              >
                <Route
                  path="/login"
                  element={
                    <AuthPage
                      type="login"
                      title={
                        <div style={{ textAlign: "center" }}>
                          <img src="/mel-icon.svg" width={48} alt={APP_NAME} />
                          <h2 style={{ margin: "8px 0 0" }}>{APP_NAME}</h2>
                          <p style={{ margin: 0, opacity: 0.7, fontSize: 13 }}>
                            {APP_TAGLINE}
                          </p>
                        </div>
                      }
                      registerLink={false}
                      forgotPasswordLink={false}
                      formProps={{
                        initialValues: { email: "", password: "" },
                      }}
                    />
                  }
                />
              </Route>
            </Routes>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
